import { forwardRef, useMemo, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { useTranslation } from "react-i18next";

import { Button, type ButtonProps } from "@/components/_atoms/button";
import { Input } from "@/components/_atoms/input";
import { Popover, PopoverPortalContent, PopoverTrigger } from "@/components/_atoms/popover";
import { useForwardedRef } from "@/hooks/useForwardRef";
import { cn } from "@/lib/cn";

import { useColorPickerPalettes } from "./use-color-picker-palettes";
import { isValidHexCode } from "./utils";

function entryDisplay(e: PaletteEntry): string {
  return typeof e === "string" ? e : e.display;
}

function entryValue(e: PaletteEntry): string {
  return typeof e === "string" ? e : e.value;
}

/** Extract the palette key from a PaletteEntry value (e.g. "$primary" → "primary"). */
function extractPaletteKey(entry: PaletteEntry): string | undefined {
  const val = entryValue(entry);
  if (val.startsWith("$") && !val.includes("-")) return val.slice(1);
  return undefined;
}

/** A palette entry can be a plain hex string or an object with a display color and a stored value (e.g. a token). */
export type PaletteEntry = string | { display: string; value: string };

interface ColorPickerHexProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  recentColors?: readonly string[];
  /** Extra palette groups to prepend before the default palettes (e.g. dashboard theme colors). */
  extraPalettes?: readonly (readonly PaletteEntry[])[];
  /** Resolves shade entries for a palette key (e.g. "primary" → 11 shade swatches). */
  shadeResolver?: (paletteKey: string) => PaletteEntry[];
}

const ColorPickerHex = forwardRef<
  HTMLInputElement,
  Omit<ButtonProps, "value" | "onChange" | "onBlur"> & ColorPickerHexProps
>(({ disabled, value, onChange, onBlur, name, className, recentColors, extraPalettes, shadeResolver, ...props }, forwardedRef) => {
  const { t } = useTranslation("common");
  const ref = useForwardedRef(forwardedRef);
  const [open, setOpen] = useState(false);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const basePalettes = useColorPickerPalettes();
  const palettes = useMemo(
    () => (extraPalettes?.length ? [...extraPalettes, ...basePalettes] : basePalettes),
    [extraPalettes, basePalettes],
  );

  const parsedValue = useMemo(() => {
    return value || "#FFFFFF";
  }, [value]);

  const allPaletteColors = useMemo(() => new Set(palettes.flat().map((c) => entryDisplay(c).toLowerCase())), [palettes]);

  const uniqueRecent = useMemo(() => {
    const seen = new Set<string>();
    const input = (recentColors ?? []).filter(
      (color) => typeof color === "string" && isValidHexCode(color) && !allPaletteColors.has(color.toLowerCase()),
    );
    const out: string[] = [];
    for (const c of input) {
      const normalized = c.toLowerCase();
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      out.push(c);
    }
    return out;
  }, [recentColors, allPaletteColors]);

  const shadeEntries = useMemo(() => {
    if (!expandedKey || !shadeResolver) return [];
    return shadeResolver(expandedKey);
  }, [expandedKey, shadeResolver]);

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild disabled={disabled} onBlur={onBlur}>
        <Button
          {...props}
          className={cn("block", className)}
          aria-label={t("colorPicker", { defaultValue: "Color picker" })}
          name={name}
          onClick={() => {
            setOpen(true);
          }}
          size="icon"
          style={{
            backgroundColor: parsedValue,
          }}
          variant="outline"
        >
          <div />
        </Button>
      </PopoverTrigger>
      <PopoverPortalContent className="w-full space-y-2">
        <HexColorPicker color={parsedValue} onChange={(v) => { setExpandedKey(null); onChange(v); }} />
        <Input
          maxLength={7}
          onChange={(e) => {
            setExpandedKey(null);
            onChange(e?.currentTarget?.value);
          }}
          ref={ref}
          value={parsedValue}
          aria-label={t("colorPickerHex", { defaultValue: "Hex color code" })}
        />
        {(palettes.length > 0 || uniqueRecent.length > 0) && (
          <div className="space-y-1.5">
            {palettes.map((palette, paletteIndex) => (
              <div key={`palette-${paletteIndex}-${entryDisplay(palette[0] ?? "")}`}>
                <div className="grid w-full gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, 1.5rem)" }}>
                  {palette.map((entry, colorIndex) => {
                    const display = entryDisplay(entry);
                    const val = entryValue(entry);
                    const paletteKey = shadeResolver ? extractPaletteKey(entry) : undefined;
                    const isExpanded = paletteKey !== undefined && paletteKey === expandedKey;
                    return (
                      <button
                        key={`${paletteIndex}-${colorIndex}-${display}`}
                        type="button"
                        className={cn(
                          "h-6 w-6 rounded-full border outline-hidden ring-offset-background focus:ring-2 focus:ring-ring",
                          isExpanded ? "border-foreground ring-2 ring-ring" : "border-border",
                        )}
                        style={{ backgroundColor: display }}
                        onClick={() => {
                          onChange(val);
                          if (paletteKey) {
                            setExpandedKey(isExpanded ? null : paletteKey);
                          } else {
                            setExpandedKey(null);
                          }
                        }}
                        aria-label={`Pick color ${display}`}
                      />
                    );
                  })}
                </div>
                {/* Shade row: appears below the palette row that contains the expanded color */}
                {shadeEntries.length > 0 && palette.some((e) => extractPaletteKey(e) === expandedKey) && (
                  <div className="mt-1 grid w-full gap-1" style={{ gridTemplateColumns: `repeat(${shadeEntries.length}, 1fr)` }}>
                    {shadeEntries.map((entry) => {
                      const display = entryDisplay(entry);
                      const val = entryValue(entry);
                      return (
                        <button
                          key={val}
                          type="button"
                          className="h-4 rounded-xs border border-border outline-hidden ring-offset-background focus:ring-1 focus:ring-ring"
                          style={{ backgroundColor: display }}
                          onClick={() => onChange(val)}
                          aria-label={`Pick shade ${val}`}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            {uniqueRecent.length > 0 && (
              <div className="grid w-full gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, 1.5rem)" }}>
                {uniqueRecent.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className="h-6 w-6 rounded-full border border-border outline-hidden ring-offset-background focus:ring-2 focus:ring-ring"
                    style={{ backgroundColor: color }}
                    onClick={() => { setExpandedKey(null); onChange(color); }}
                    aria-label={`Pick color ${color}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </PopoverPortalContent>
    </Popover>
  );
});
ColorPickerHex.displayName = "ColorPickerHex";

export { ColorPickerHex };
